import React, { useState } from "react";
import { api } from "@/lib/api";
import { Mic, Loader2 } from "lucide-react";
import { Disclaimer } from "@/components/Badges";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const SCENARIOS = {
  en: [
    { id: "status", title: "Status hearing", prompt: "You're at a status hearing. The judge asks: 'Can both sides tell me where you are in the case?' You are the self-represented defendant." },
    { id: "motion", title: "Motion hearing", prompt: "You filed a motion to vacate a default judgment. The judge asks: 'Why should I grant your motion?'" },
    { id: "continuance", title: "Asking for a continuance", prompt: "You need more time. Politely ask the judge for a continuance and give a real-sounding reason." },
    { id: "clarify", title: "Asking to clarify", prompt: "The opposing attorney says something confusing. Practice asking the judge for clarification." },
  ],
  es: [
    { id: "status", title: "Audiencia de estado", prompt: "Estás en una audiencia de estado. El juez pregunta: '¿Pueden ambas partes decirme en qué punto están del caso?' Eres el demandado auto-representado." },
    { id: "motion", title: "Audiencia de moción", prompt: "Presentaste una moción para anular una sentencia en rebeldía. El juez pregunta: '¿Por qué debo otorgar tu moción?'" },
    { id: "continuance", title: "Pedir un aplazamiento", prompt: "Necesitas más tiempo. Pídele al juez un aplazamiento con respeto y da una razón realista." },
    { id: "clarify", title: "Pedir aclaración", prompt: "El abogado contrario dice algo confuso. Practica pedirle al juez que aclare." },
  ],
};

const PHRASES = {
  en: [
    "Your Honor, may I clarify the question?",
    "I do not understand the question.",
    "May I refer to my notes?",
    "I respectfully disagree.",
    "Could you please repeat that?",
    "I would like to ask for an opportunity to respond.",
  ],
  es: [
    "Su Señoría, ¿puedo aclarar la pregunta?",
    "No entiendo la pregunta.",
    "¿Puedo consultar mis notas?",
    "Respetuosamente estoy en desacuerdo.",
    "¿Podría repetir eso, por favor?",
    "Me gustaría pedir una oportunidad para responder.",
  ],
};

export default function Practice() {
  const { lang, t } = useLang();
  const { refreshMe } = useAuth();
  const scenarios = SCENARIOS[lang] || SCENARIOS.en;
  const phrases = PHRASES[lang] || PHRASES.en;
  const [selected, setSelected] = useState(scenarios[0]);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");

  // Reset selection when language changes
  React.useEffect(() => { setSelected(scenarios[0]); setAnswer(""); setFeedback(""); }, [lang]);

  const submit = async () => {
    if (!answer.trim()) return;
    setLoading(true);
    setFeedback("");
    try {
      const message = lang === "es"
        ? `Estoy practicando una respuesta en la corte. Escenario: ${selected.prompt}\n\nMi respuesta: "${answer}"\n\nPor favor dame retroalimentación corta y amable en español sobre:\n1) claridad y organización\n2) respeto y tono de sala\n3) si realmente respondí la pregunta\n4) una mejora específica que puedo intentar.\n\nNO me digas qué argumento legal hacer ni predigas resultados.`
        : `I am practicing a courtroom response. Scenario: ${selected.prompt}\n\nMy response: "${answer}"\n\nPlease give me short, kind feedback on:\n1) clarity and organization\n2) respect and courtroom tone\n3) whether I actually answered the question\n4) one specific improvement I can try.\n\nDo NOT tell me what legal argument to make or predict outcomes.`;
      const res = await api.post("/deborah/chat", { message, feature: "practice" });
      setFeedback(res.data.text);
      if (refreshMe) await refreshMe();
    } catch (e) {
      setFeedback(e.response?.data?.detail || (lang === "es" ? "Algo salió mal. Intenta de nuevo." : "Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Mic className="w-6 h-6 text-[#C85A32]" /> {t("practice.title")}
        </h1>
        <p className="text-[#4A525A] mt-1">{t("practice.subtitle")}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {scenarios.map((s) => (
          <button
            key={s.id}
            data-testid={`scenario-${s.id}`}
            onClick={() => { setSelected(s); setAnswer(""); setFeedback(""); }}
            className={`pod-focus px-4 py-2 rounded-full text-sm font-bold border-2 transition-colors ${
              selected.id === s.id
                ? "bg-[#C85A32] text-white border-[#C85A32]"
                : "bg-white text-[#1C2024] border-[#E4DDD3] hover:border-[#C85A32]"
            }`}
          >
            {s.title}
          </button>
        ))}
      </div>

      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
        <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("practice.scenario")}</div>
        <p className="mt-2 text-lg">{selected.prompt}</p>
      </div>

      <textarea
        rows={5}
        data-testid="practice-answer"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder={lang === "es" ? 'Escribe o pega lo que dirías. Empieza con "Su Señoría..."' : 'Type or paste what you would say. Start with "Your Honor..."'}
        className="w-full rounded-2xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-4 bg-white"
      />
      <button
        onClick={submit}
        disabled={loading}
        data-testid="practice-submit"
        className="pod-focus bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center gap-2"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {t("practice.get_feedback")}
      </button>

      {feedback && (
        <div className="bg-[#FAF5EC] border-2 border-[#E4DDD3] rounded-2xl p-5 whitespace-pre-wrap" data-testid="practice-feedback">
          {feedback}
        </div>
      )}

      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-5">
        <div className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{lang === "es" ? "Frases útiles" : "Helpful phrases"}</div>
        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {phrases.map((p, idx) => (
            <li key={idx} className="text-[#1C2024]">• "{p}"</li>
          ))}
        </ul>
      </div>

      <Disclaimer />
    </div>
  );
}
